import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the button link structure
interface ButtonLink {
  buttonImage: string | null;
  buttonLink: string | null;
  buttonText: string | null ;
  buttonType: string | null;
}

// Define the main context data structure
interface ContextData {
  buttonLink: ButtonLink;
  title: string | null;
}

// Define the context type with methods to update the data
interface AppContextType {
  data: ContextData;
  updateData: (newData: Partial<ContextData>) => void;
  updateButtonLink: (buttonData: Partial<ButtonLink>) => void;
  updateTitle: (title: string) => void;
  resetData: () => void;
}

// Default data structure
const defaultData: ContextData = {
  buttonLink: {
    buttonImage: null,
    buttonLink: null,
    buttonText: null,
    buttonType: null
  },
  title: null
};

// Create the context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component props
interface AppContextProviderProps {
  children: ReactNode;
  initialData?: Partial<ContextData>;
}

// Provider component
export const AppContextProvider: React.FC<AppContextProviderProps> = ({ 
  children, 
  initialData 
}) => {
  const [data, setData] = useState<ContextData>({
    ...defaultData,
    ...initialData
  });

  // Update entire data object
  const updateData = (newData: Partial<ContextData>) => {
    setData(prevData => ({
      ...prevData,
      ...newData,
      buttonLink: {
        ...prevData.buttonLink,
        ...(newData.buttonLink || {})
      }
    }));
  };

  // Update only button link data
  const updateButtonLink = (buttonData: Partial<ButtonLink>) => {
    setData(prevData => ({
      ...prevData,
      buttonLink: {
        ...prevData.buttonLink,
        ...buttonData
      }
    }));
  };

  // Update only title
  const updateTitle = (title: string) => {
    setData(prevData => ({
      ...prevData,
      title
    }));
  };

  // Reset to default data
  const resetData = () => {
    setData(defaultData);
  };

  const contextValue: AppContextType = {
    data,
    updateData,
    updateButtonLink,
    updateTitle,
    resetData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};

// Export types for use in other components
export type { ContextData, ButtonLink, AppContextType };